import { MouseEvent, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Menu, MenuItem, Stack, Box, Button, Typography } from '@mui/material';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ctxReport } from '@/contexts/ReportCtx';
import { ReportTabValue } from '@/types';
import { ReportTabs } from './ReportPopup';

export default function CategorySelector({
  activeTab,
  setActiveTab,
  isPhone,
}: {
  activeTab: ReportTabs;
  setActiveTab: (tab: ReportTabs) => void;
  isPhone?: boolean;
}) {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const reportCtx = useContext(ctxReport);

  function handleClick(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSelect(option: ReportTabs) {
    setActiveTab(option);
    handleClose();
  }

  function getLabelText() {
    switch (activeTab) {
      case 'TargetTab':
        return t('report.tab.target.title');
      case 'DriverTab':
        return t('report.tab.driver.title');
      case 'StatusTab':
        return t('report.tab.status');
    }
  }

  function getLabel() {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {getLabelText()}
        </Typography>
        <KeyboardArrowDownIcon fontSize="small" />
      </Stack>
    );
  }

  function getIcon() {
    switch (activeTab) {
      case 'TargetTab':
        return <DirectionsCarOutlinedIcon className="category-selector-icon target" />;
      case 'DriverTab':
        return <PersonOutlineOutlinedIcon className="category-selector-icon driver" />;
      case 'StatusTab':
        return <DescriptionOutlinedIcon className="category-selector-icon status" />;
    }
  }

  function getClass() {
    switch (activeTab) {
      case 'TargetTab':
        return 'target';
      case 'DriverTab':
        return 'driver';
      case 'StatusTab':
        return 'status';
    }
  }

  return isPhone ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 2,
        margin: '0% 8%',
      }}
    >
      <Stack direction="column" spacing={2} mt={2} sx={{ width: '100%', marginTop: '5svh' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DirectionsCarOutlinedIcon />}
          fullWidth
          sx={{
            borderRadius: 5,
            justifyContent: 'center',
            textAlign: 'center',
            paddingRight: 0,
            pl: 2.5,
            '& .MuiButton-startIcon': {
              position: 'absolute',
              left: 16,
              margin: 0,
            },
          }}
          onClick={() => {
            setActiveTab('TargetTab');
            reportCtx?.setSelectedDrivers([]);
            reportCtx?.setTab(ReportTabValue.entitySelect);
          }}
          data-qa-element-id="select_vehicle_category_button"
        >
          {t('report.tab.target.title')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          startIcon={<PersonOutlineOutlinedIcon />}
          sx={{
            borderRadius: 5,
            justifyContent: 'center',
            textAlign: 'center',
            paddingRight: 0,
            pl: 2.5,
            '& .MuiButton-startIcon': {
              position: 'absolute',
              left: 16,
              margin: 0,
            },
          }}
          onClick={() => {
            setActiveTab('DriverTab');
            reportCtx?.setSelectedTargets([]);
            reportCtx?.setTab(ReportTabValue.entitySelect);
          }}
          data-qa-element-id="select_driver_category_button"
        >
          {t('report.tab.driver.title')}
        </Button>
      </Stack>
    </Box>
  ) : (
    <div className="active-tab-selector-conatiner">
      <Chip
        className={`active-tab-selector ${getClass()}`}
        label={getLabel()}
        onClick={handleClick}
        clickable
        icon={getIcon()}
        sx={{ borderRadius: '16px' }}
        data-qa-element-id="category_selector"
      />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem value="TargetTab" onClick={() => handleSelect('TargetTab')} data-qa-element-id="target_menu_item">
          <Stack direction="row" gap={1} className="report-tab-selection-option">
            <DirectionsCarOutlinedIcon />
            <Typography>{t('report.tab.target.title')}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem value="DriverTab" onClick={() => handleSelect('DriverTab')} data-qa-element-id="driver_menu_item">
          <Stack direction="row" gap={1} className="report-tab-selection-option">
            <PersonOutlineOutlinedIcon />
            <Typography>{t('report.tab.driver.title')}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          value="StatusTab"
          onClick={() => handleSelect('StatusTab')}
          data-qa-element-id="report_owerview_menu_item"
        >
          <Stack direction="row" gap={1} className="report-tab-selection-option">
            <DescriptionOutlinedIcon />
            <Typography>{t('report.tab.status')}</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </div>
  );
}
